import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  AiOutlineStar,
  AiOutlineCheckCircle,
  AiOutlineLineChart,
} from 'react-icons/ai';
import CounterUp from '../Components/counterUp';

import image0 from '../images/Sabean/image_0.jpg';
import image1 from '../images/Sabean/Sabeanby_AronSimeneh-2124.jpg';
import image2 from '../images/Sabean/Sabeanby_AronSimeneh-2343.jpg';
import image3 from '../images/Sabean/Sabeanby_AronSimeneh-2946.jpg';
import image4 from '../images/Sabean/Sabeanby_AronSimeneh-2608.jpg';
import image5 from '../images/Sabean/Sabeanby_AronSimeneh-2551.jpg';
import image6 from '../images/Sabean/Sabeanby_AronSimeneh-2377.jpg';
import image7 from '../images/Sabean/Sabeanby_AronSimeneh-3211.jpg';
import image8 from '../images/Sabean/Sabeanby_AronSimeneh-3316.jpg';
import image9 from '../images/Sabean/Sabeanby_AronSimeneh-4053.jpg';

// Main new images
import newImage1 from '../images/Sabean/Sabean_drab.jpeg';
import newImage2 from '../images/Sabean/Saben_kbatt.jpeg';
import newImage3 from '../images/Sabean/Sabean_smartmet.jpeg';

// Certificate and extra gallery images
import galleryNew2 from '../images/Sabean/certificate1.jpg';
import galleryNew3 from '../images/Sabean/certificate2.jpg';
import galleryNew4 from '../images/Sabean/sabean about 22.jpg';

const backgroundImages = [
  newImage1,
  newImage2,
  newImage3,
  image0,
  image1,
  image2,
  image3,
  image4,
  image5,
];

const galleryImages = [
  // Important images with descriptions
  { src: newImage1, label: 'Prime Minister Dr. Abiy Ahmed’s Visit' },
  {
    src: newImage2,
    label: 'Official Visit by the President and Minister of Industry',
  },
  { src: newImage3, label: 'Smart Water Meter Technology' },

  // Existing images — normal gallery
  { src: image0 },
  { src: image2 },
  { src: image3 },
  { src: image4 },
  { src: image5 },
  { src: image6 },
  { src: image7 },
  { src: image8 },
  { src: image9 },

  // New gallery image
  { src: galleryNew4 },

  // Certificates — open full image on click
  {
    src: galleryNew2,
    label: 'ISO 4064:2021',
    isCertificate: true,
  },
  {
    src: galleryNew3,
    label: 'ISO 9001:2015',
    isCertificate: true,
  },
];

const features = [
  {
    icon: <AiOutlineStar size={80} color="#0077b6" className="mx-auto" />,
    title: 'Quality and Reliability',
    desc: 'SABEAN offers state-of-the-art water meters that comply with ISO 4064:2021 standards, ensuring accuracy, reliability, and long-term performance. We are also ISO 9001:2015 certified for our Quality Management System.',
  },
  {
    icon: (
      <AiOutlineCheckCircle size={80} color="#0077b6" className="mx-auto" />
    ),
    title: 'Environmental Commitment',
    desc: 'Our water meters support sustainability, promoting efficient water usage and conservation.',
  },
  {
    icon: <AiOutlineLineChart size={80} color="#0077b6" className="mx-auto" />,
    title: 'Import Substitution & Revenue Growth',
    desc: 'SABEAN cuts reliance on imported meters, boosts local manufacturing, and tackles issues affecting utility revenues.',
  },
];

const GalleryCard = ({ src, label, isCertificate, onViewImage }) => (
  <div
    onClick={isCertificate ? onViewImage : undefined}
    className={`relative mt-8 w-full sm:w-[47%] lg:w-[30%] group overflow-hidden rounded-2xl shadow-md bg-white ${
      isCertificate ? 'cursor-zoom-in' : ''
    }`}
  >
    <img
      src={src}
      alt={label || 'SABEAN gallery image'}
      className="w-full h-64 sm:h-72 md:h-80 object-cover object-center transition duration-500 group-hover:scale-105 group-hover:brightness-75"
    />

    {label && (
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
        <p className="text-white text-sm md:text-base font-medium">{label}</p>
      </div>
    )}

    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition duration-300">
      {isCertificate ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onViewImage();
          }}
          className="bg-[#0077b6] text-white px-5 py-2 rounded-lg hover:bg-[#005f8e] transition shadow-lg"
        >
          View Certificate
        </button>
      ) : (
        <Link to="/about-us">
          <button className="bg-[#0077b6] text-white px-5 py-2 rounded-lg hover:bg-[#005f8e] transition shadow-lg">
            Know More
          </button>
        </Link>
      )}
    </div>
  </div>
);

const Home = () => {
  const [currentBg, setCurrentBg] = useState(0);
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % backgroundImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const goToSlide = (index) => {
    setCurrentBg(index);
  };

  const goToPrevious = () => {
    setCurrentBg((prev) =>
      prev === 0 ? backgroundImages.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentBg((prev) => (prev + 1) % backgroundImages.length);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative mt-24 md:mt-28 min-h-[76vh] sm:min-h-[82vh] lg:min-h-[90vh] overflow-hidden flex items-center">
        <img
          src={backgroundImages[currentBg]}
          alt="SABEAN water meter background"
          className="absolute inset-0 w-full h-full object-cover object-center transition-all duration-1000"
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"></div>
        <div className="absolute inset-0 bg-black/20"></div>

        <motion.div
          className="relative z-10 w-full px-5 sm:px-8 md:px-16 lg:px-24"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.6, ease: 'easeInOut' }}
        >
          <div className="max-w-3xl text-white">
            <p className="uppercase tracking-[0.3em] text-xs sm:text-sm font-semibold text-sky-200 mb-4">
              SABEAN Base Metal Engineering PLC
            </p>

            <h1 className="font-edwardian text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold leading-tight text-center md:text-left">
              We Value Every Droplet of Water!
            </h1>

            <p className="font-serif text-base sm:text-lg md:text-xl mt-6 text-gray-100 leading-relaxed text-center md:text-left max-w-2xl">
              Welcome to SABEAN, Ethiopia&apos;s sole manufacturer of
              high-quality water meters. We are committed to innovative
              solutions for efficient water management and sustainability.
            </p>

            <div className="mt-8 flex gap-4 flex-wrap justify-center md:justify-start">
              <Link to="/products/water-meters">
                <button className="bg-[#0077b6] hover:bg-[#005f8e] text-white font-semibold px-6 py-3 rounded-lg transition duration-300 shadow-lg">
                  Explore Products
                </button>
              </Link>

              <Link to="/contact-us">
                <button className="border border-white text-white hover:bg-white hover:text-[#0077b6] font-semibold px-6 py-3 rounded-lg transition duration-300">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </motion.div>

        <button
          onClick={goToPrevious}
          className="hidden sm:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/20 hover:bg-white/40 text-white items-center justify-center backdrop-blur-sm transition"
          aria-label="Previous slide"
        >
          &#10094;
        </button>

        <button
          onClick={goToNext}
          className="hidden sm:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/20 hover:bg-white/40 text-white items-center justify-center backdrop-blur-sm transition"
          aria-label="Next slide"
        >
          &#10095;
        </button>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-wrap justify-center gap-2 z-20 px-4">
          {backgroundImages.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`h-2.5 rounded-full transition-all duration-300 ${
                i === currentBg ? 'w-8 bg-white' : 'w-2.5 bg-white/45'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Key Features */}
      <section className="font-serif justify-center items-center md:mx-12 mx-4 mt-14">
        <h3 className="text-4xl sm:text-5xl text-[#0077b6] text-center mb-2">
          Key Features
        </h3>

        <p className="text-center text-gray-500 mb-8">
          What sets SABEAN apart
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="text-center shadow-lg p-8 md:p-10 rounded-2xl my-4 bg-white border-t-4 border-[#0077b6] transition duration-300 ease-in-out transform hover:scale-105 hover:shadow-xl"
            >
              {feature.icon}

              <h3 className="text-lg font-semibold pt-6 pb-2 text-[#0077b6]">
                {feature.title}
              </h3>

              <p className="py-2 text-gray-600 leading-relaxed">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section
        className="font-serif justify-center items-center my-12 bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: `url(${image7})` }}
      >
        <div className="absolute inset-0 bg-[#03045e]/65"></div>

        <div className="relative z-10 py-16">
          <CounterUp />
        </div>
      </section>

      {/* Gallery */}
      <section className="font-serif justify-center items-center md:mx-12 mx-4">
        <h3 className="text-4xl sm:text-5xl text-[#0077b6] text-center mt-4 mb-2">
          Gallery
        </h3>

        <p className="text-center text-gray-500 mb-4">
          A glimpse into SABEAN&apos;s work
        </p>

        <div className="flex flex-wrap gap-4 md:gap-8 items-center justify-center mb-12">
          {galleryImages.map((item, index) => (
            <GalleryCard
              key={index}
              src={item.src}
              label={item.label}
              isCertificate={item.isCertificate}
              onViewImage={() => setSelectedCertificate(item)}
            />
          ))}
        </div>
      </section>

      {/* Certificate Full Image Preview */}
      {selectedCertificate && (
        <div
          className="fixed inset-0 bg-black/85 z-[9999] flex items-center justify-center p-4"
          onClick={() => setSelectedCertificate(null)}
        >
          <div
            className="relative max-w-5xl w-full max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setSelectedCertificate(null)}
              className="absolute -top-12 right-0 bg-white text-[#03045e] px-4 py-2 rounded-lg font-semibold hover:bg-gray-200 transition"
            >
              Close
            </button>

            <img
              src={selectedCertificate.src}
              alt={selectedCertificate.label}
              className="w-full max-h-[90vh] object-contain rounded-xl bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;